import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Button,
  Divider,
  Chip,
} from "@mui/material";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import Imagezoomer from "../../Components/Imagezoomer/Imagezoomer";
import ThumbStrip from "../../Components/ThumbStrip/ThumbStrip";
import { postData, getData } from "../../Webservice/Webservice";
import { useCart } from "../../context/CartContext";
import { getToken, getUser } from "../../Utils/authHelper";
import ProductCard from "../../Components/ProductCard/ProductCard";
import ComboCard from "../../Components/ComboCard/ComboCard";

const rubikfont = {
  fontFamily: "Rubik, sans-serif",
};

const ComboDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUser();
  const isBusiness = user?.customerType === 'business';

  const [combo, setCombo] = useState(location.state?.combo || null);
  const [loading, setLoading] = useState(!combo);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [similarCombos, setSimilarCombos] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [descOpen, setDescOpen] = useState(false);
  const { fetchCartCount } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchComboData = async () => {
      try {
        setLoading(true);
        const res = await getData(`combo-offer/${id}`, {}, "", false);
        if (res && res.success && res.data) {
          setCombo(res.data);
        } else if (!combo) {
          toast.error("Combo data not found");
          navigate("/");
        }
      } catch (err) {
        console.error("Failed to fetch combo:", err);
        if (!combo) {
          toast.error("Combo data not found");
          navigate("/");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchComboData();
  }, [id]);

  useEffect(() => {
    const fetchSimilarCombos = async () => {
      try {
        const res = await getData("combo-offer/all", {}, "", false);
        if (res && res.success && Array.isArray(res.data)) {
          setSimilarCombos(res.data.filter((c) => c._id !== id));
        }
      } catch (e) {
        console.error("Failed to fetch similar combos:", e);
      }
    };
    fetchSimilarCombos();
  }, [id]);

  if (!combo) return null;

  const mediaItems = [];
  const API_URL = "https://api.glamcompany.in/api/v1/";
  if (combo.product_images && combo.product_images.length > 0) {
    combo.product_images.forEach((img) => {
      const parsedSrc = img.startsWith("http")
        ? img
        : `${API_URL.replace(/\/api\/v1\/?$/, "")}/${img.replace(/^\//, "")}`;
      mediaItems.push({ type: "image", src: parsedSrc });
    });
  } else {
    (combo.products || []).forEach((item) => {
      const imgs = item.product_id?.product_images;
      if (imgs && imgs.length > 0) {
        mediaItems.push({ type: "image", src: imgs[0] });
      }
    });
  }
  const currentMedia = mediaItems[selectedImage] || null;

  const displaySellingPrice = isBusiness ? (combo.b2b_sellingPrice || combo.b2c_sellingPrice) : combo.b2c_sellingPrice;
  const displayMrp = isBusiness ? (combo.b2b_mrp || combo.b2c_mrp) : combo.b2c_mrp;

  const isComboInStock = (() => {
    if (combo.inStock === false || combo.isActive === false) return false;
    if (!combo.products || combo.products.length === 0) return false;
    return combo.products.every(item => {
      const p = item.product_id;
      if (!p) return false;
      if (p.inStock === false || p.status === 'inactive' || p.isDeleted) return false;
      const reqQty = Number(item.quantity) || 1;
      if (typeof p.availableQuantity === 'number' && p.availableQuantity < reqQty) return false;
      return true;
    });
  })();

  const totalSavings =
    displayMrp && displaySellingPrice
      ? displayMrp - displaySellingPrice
      : 0;

  const handleAddToCart = async () => {
    const tokenValue = getToken();
    const token = tokenValue ? `Bearer ${tokenValue}` : "";

    if (!token) {
      navigate("/signin");
      return;
    }

    try {
      setAddingToCart(true);
      const qty = Math.max(1, Math.floor(Number(quantity) || 1));

      const response = await postData(
        "cart/add-to-cart",
        { product_id: combo._id, quantity: qty, isCombo: true },
        token,
        false,
        false
      );

      if (
        response &&
        (response.success ||
          response.cart ||
          response.message === "Cart updated")
      ) {
        fetchCartCount();
        toast.success("Combo added to cart!");
      } else if (response && response.message) {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error updating cart:", error);
      toast.error("Failed to update cart. Please try again.");
    } finally {
      setAddingToCart(false);
    }
  };

  const handleBuyNow = async () => {
    const tokenValue = getToken();
    const token = tokenValue ? `Bearer ${tokenValue}` : "";

    if (!token) {
      navigate("/signin");
      return;
    }

    try {
      const qty = Math.max(1, Math.floor(Number(quantity) || 1));
      const payload = {
        productId: combo._id,
        quantity: qty,
        isCombo: true,
      };

      const response = await postData("order/buy-now/checkout", payload, token);

      if (response && response.checkout) {
        navigate("/checkout", { state: { checkoutData: response } });
      } else {
        toast.error(response?.message || "Failed to proceed to checkout.");
      }
    } catch (error) {
      console.error("Error processing buy now:", error);
      toast.error("Failed to proceed to checkout.");
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#ffffff", ...rubikfont, pb: 6 }}>
      <Box sx={{ maxWidth: "1440px", margin: "0 auto", padding: { xs: "16px", md: "32px 24px" } }}>

        {/* 3-COLUMN FLEX LAYOUT */}
        <Box
          sx={{
            display: "flex",
            gap: { xs: 0, md: "20px" },
            flexDirection: { xs: "column", md: "row" },
            alignItems: "flex-start",
          }}
        >

          {/* COLUMN 1: IMAGE GALLERY */}
          <Box
            sx={{
              width: { xs: "100%", md: "360px" },
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: "8px",
                flexDirection: { xs: "column-reverse", md: "row" },
              }}
            >
              {/* Thumbnails: 6 at a time, with buttons for the rest */}
              {mediaItems.length > 0 && (
                <ThumbStrip
                  items={mediaItems}
                  selected={selectedImage}
                  onSelect={setSelectedImage}
                  perPage={6}
                  gap={8}
                  thumbSize={54}
                  renderThumb={(item, idx, active) => (
                    <Box
                      sx={{
                        width: "54px",
                        height: "54px",
                        flexShrink: 0,
                        borderRadius: "4px",
                        border: active ? "2px solid #e77600" : "1px solid #D5D9D9",
                        cursor: "pointer",
                        p: "3px",
                        bgcolor: "#fff",
                        transition: "border-color 0.15s",
                        "&:hover": { borderColor: "#e77600" },
                      }}
                    >
                      <img src={item.src} alt={`Thumb ${idx + 1}`} style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
                    </Box>
                  )}
                />
              )}

              {/* Main Image */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  border: "1px solid #D5D9D9",
                  borderRadius: "4px",
                  bgcolor: "#fff",
                  height: { xs: "300px", md: "460px" },
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Imagezoomer
                  src={currentMedia?.src || ""}
                  alt={combo.title}
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </Box>
            </Box>
          </Box>

          {/* COLUMN 2: PRODUCT INFO */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "18px", md: "21px" },
                fontWeight: 400,
                color: "#0F1111",
                lineHeight: 1.35,
                mb: "6px",
                ...rubikfont,
              }}
            >
              {combo.title}
            </Typography>

            <Typography variant="body2" sx={{ color: "#007185", mb: "4px" }}>
              Combo Pack &bull; {combo.products?.length || 0} Items
            </Typography>

            <Divider sx={{ my: "12px" }} />

            {/* Price */}
            <Box sx={{ mb: "14px" }}>
              {combo.discount_percentage > 0 && (
                <Box sx={{ display: "inline-block", bgcolor: "#cc0c39", px: "6px", py: "2px", borderRadius: "3px", mb: "6px" }}>
                  <Typography sx={{ fontSize: "11px", color: "#fff", fontWeight: 700 }}>
                    -{combo.discount_percentage}% OFF
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "2px" }}>
                <Typography sx={{ fontSize: "13px", color: "#0F1111", lineHeight: 1, mt: "6px" }}>Rs.</Typography>
                <Typography sx={{ fontSize: "30px", fontWeight: 400, color: "#0F1111", lineHeight: 1 }}>
                  {displaySellingPrice}
                </Typography>
              </Box>
              {displayMrp && displayMrp > displaySellingPrice && (
                <Typography variant="body2" sx={{ color: "#565959", mt: "4px" }}>
                  M.R.P.:{" "}
                  <Box component="span" sx={{ textDecoration: "line-through" }}>Rs.{displayMrp}</Box>
                  <Box component="span" sx={{ color: "#388e3c", fontWeight: 600, ml: "6px" }}>
                    You save Rs.{totalSavings}
                  </Box>
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: "#565959", display: "block", mt: "4px" }}>
                Inclusive of all taxes
              </Typography>
            </Box>

            <Divider sx={{ mb: "12px" }} />

            {/* Trust badges */}
            <Box sx={{ display: "flex", gap: "20px", mb: "14px", flexWrap: "wrap" }}>
              {[
                { icon: <VerifiedUserOutlinedIcon sx={{ fontSize: "18px", color: "#007185" }} />, label: "100% Authentic" },
                { icon: <LocalShippingOutlinedIcon sx={{ fontSize: "18px", color: "#007185" }} />, label: "Express Delivery" },
                { icon: <LocalOfferOutlinedIcon sx={{ fontSize: "18px", color: "#007185" }} />, label: "Best Price" },
              ].map((feat, idx) => (
                <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  {feat.icon}
                  <Typography sx={{ fontSize: "12px", color: "#565959", fontWeight: 500 }}>
                    {feat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* COLUMN 3: BUY BOX */}
          <Box
            sx={{
              width: { xs: "100%", lg: "240px" },
              flexShrink: 0,
              mt: { xs: "20px", lg: 0 },
            }}
          >
            <Box
              sx={{
                border: "1px solid #D5D9D9",
                borderRadius: "8px",
                p: "16px",
                bgcolor: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "2px", mb: "4px" }}>
                <Typography sx={{ fontSize: "13px", color: "#0F1111", mt: "5px" }}>Rs.</Typography>
                <Typography sx={{ fontSize: "28px", fontWeight: 400, color: "#0F1111", lineHeight: 1 }}>
                  {displaySellingPrice}
                </Typography>
              </Box>
              {displayMrp && displayMrp > displaySellingPrice && (
                <Typography sx={{ fontSize: "12px", color: "#565959", mb: "8px" }}>
                  M.R.P.:{" "}
                  <Box component="span" sx={{ textDecoration: "line-through" }}>Rs.{displayMrp}</Box>
                </Typography>
              )}

              <Typography sx={{ fontSize: "13px", color: "#007185", mb: "4px" }}>FREE delivery</Typography>
              {isComboInStock ? (
                <Typography sx={{ fontSize: "16px", color: "#007600", mb: "12px", fontWeight: 500 }}>In stock</Typography>
              ) : (
                <Typography sx={{ fontSize: "16px", color: "#B12704", mb: "12px", fontWeight: 600 }}>Currently unavailable / Out of stock</Typography>
              )}

              {isComboInStock && (
                <Box sx={{ mb: "12px" }}>
                  <Typography sx={{ fontSize: "13px", color: "#0F1111", mb: "6px" }}>Quantity:</Typography>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      border: "1px solid #D5D9D9",
                      borderRadius: "8px",
                      bgcolor: "#F0F2F2",
                      height: "34px",
                      overflow: "hidden",
                      boxShadow: "0 2px 5px rgba(15,17,17,.1)",
                    }}
                  >
                    <Box
                      component="button"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", px: "10px", height: "100%", display: "flex", alignItems: "center", "&:hover": { bgcolor: "#ddd" } }}
                    >
                      <RemoveIcon sx={{ fontSize: "14px" }} />
                    </Box>
                    <Box
                      sx={{ px: "14px", bgcolor: "#fff", height: "100%", display: "flex", alignItems: "center", borderLeft: "1px solid #D5D9D9", borderRight: "1px solid #D5D9D9", minWidth: "36px", justifyContent: "center" }}
                    >
                      <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>{quantity}</Typography>
                    </Box>
                    <Box
                      component="button"
                      onClick={() => setQuantity(quantity + 1)}
                      sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", px: "10px", height: "100%", display: "flex", alignItems: "center", "&:hover": { bgcolor: "#ddd" } }}
                    >
                      <AddIcon sx={{ fontSize: "14px" }} />
                    </Box>
                  </Box>
                </Box>
              )}

              <Button
                fullWidth
                variant="contained"
                onClick={handleAddToCart}
                disabled={addingToCart || !isComboInStock}
                sx={{
                  bgcolor: isComboInStock ? "#FFD814" : "#E7E9EC",
                  color: isComboInStock ? "#0F1111" : "#888",
                  fontWeight: 400,
                  py: "8px",
                  mb: "10px",
                  borderRadius: "100px",
                  boxShadow: isComboInStock ? "0 2px 5px rgba(213,217,217,.5)" : "none",
                  "&:hover": { bgcolor: isComboInStock ? "#F7CA00" : "#E7E9EC" },
                  textTransform: "none",
                  fontSize: "14px",
                }}
              >
                {!isComboInStock ? "Out of Stock" : addingToCart ? "Adding..." : "Add to Cart"}
              </Button>

              <Button
                fullWidth
                variant="contained"
                onClick={handleBuyNow}
                disabled={!isComboInStock}
                sx={{
                  bgcolor: isComboInStock ? "#FFA41C" : "#F0F2F2",
                  color: isComboInStock ? "#0F1111" : "#aaa",
                  fontWeight: 400,
                  py: "8px",
                  mb: "14px",
                  borderRadius: "100px",
                  boxShadow: isComboInStock ? "0 2px 5px rgba(213,217,217,.5)" : "none",
                  "&:hover": { bgcolor: isComboInStock ? "#FA8900" : "#F0F2F2" },
                  textTransform: "none",
                  fontSize: "14px",
                }}
              >
                Buy Now
              </Button>

              <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <LockOutlinedIcon sx={{ fontSize: "14px", color: "#565959" }} />
                <Typography sx={{ fontSize: "12px", color: "#565959" }}>Secure transaction</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ── DETAILED PRODUCTS BREAKDOWN ──────────────────────── */}
        {combo.products && combo.products.length > 0 && (
          <Box sx={{ mt: { xs: 3, md: 4 }, p: { xs: 1.75, md: 2.5 }, borderRadius: "16px", bgcolor: "#fff", border: "1px solid #ece6d8" }}>
            {/* Heading */}
            <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: { xs: 0.5, md: 1.5 }, mb: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Inventory2OutlinedIcon sx={{ color: "#C8A054", fontSize: "22px" }} />
                <Typography variant="h2" sx={{ fontSize: { xs: "18px", md: "21px" }, fontWeight: 700, color: "#0f172a", ...rubikfont }}>
                  Detailed Products Breakdown
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "12.5px", color: "#64748b", ...rubikfont }}>
                {combo.products.length} product{combo.products.length === 1 ? "" : "s"} in this pack
              </Typography>
            </Box>

            {/* Product picker: thumbnails instead of text tabs */}
            <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 1, mb: 1.5, scrollbarWidth: "thin", "&::-webkit-scrollbar": { height: 3 }, "&::-webkit-scrollbar-thumb": { background: "#d9c9a3", borderRadius: 2 } }}>
              {combo.products.map((item, idx) => {
                const pp = item.product_id;
                const on = activeTab === idx;
                return (
                  <Box
                    key={idx}
                    onClick={() => { setActiveTab(idx); setDescOpen(false); }}
                    sx={{
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      pl: 0.75,
                      pr: 1.5,
                      py: 0.6,
                      borderRadius: "12px",
                      cursor: "pointer",
                      maxWidth: 230,
                      border: on ? "1.5px solid #C8A054" : "1px solid #e8e2d4",
                      bgcolor: on ? "#fbf6ea" : "#fff",
                      transition: "all .2s",
                      "&:hover": { borderColor: "#C8A054" },
                    }}
                  >
                    <Box component="img" src={pp?.product_images?.[0] || ""} alt="" sx={{ width: 36, height: 36, objectFit: "contain", borderRadius: "8px", bgcolor: "#fff", border: "1px solid #f1ede2" }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: "12.5px", fontWeight: on ? 700 : 600, color: on ? "#7a5c14" : "#334155", lineHeight: 1.25, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", ...rubikfont }}>
                        {pp?.title || `Product ${idx + 1}`}
                      </Typography>
                      <Typography sx={{ fontSize: "10.5px", color: "#94a3b8", ...rubikfont }}>× {item.quantity || 1}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Selected product */}
            {(() => {
              const activeItem = combo.products[activeTab] || combo.products[0];
              const p = activeItem?.product_id;
              if (!p) return null;

              const pPrice = isBusiness ? (p.b2b_sellingPrice || p.b2c_sellingPrice) : p.b2c_sellingPrice;
              const pMrp = isBusiness ? (p.b2b_mrp || p.b2c_mrp) : p.b2c_mrp;
              const facts = [
                { label: "Code", value: p.productCode },
                { label: "Brand", value: p.brand },
                { label: "Size", value: p.unitValue ? `${p.unitValue} ${p.unitName || ""}` : null },
                { label: "Pack", value: p.container_type },
                { label: "Ideal for", value: p.ideal_for },
                { label: "In bundle", value: `${activeItem.quantity || 1} unit(s)` },
                { label: "Stock", value: p.inStock !== false ? "In stock" : "Out of stock" },
              ].filter((x) => x.value);
              const plainDesc = (p.description || "").replace(/<[^>]*>/g, "");
              const longDesc = plainDesc.length > 320;

              const block = (title, html, tone) => (
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: "#0f172a", mb: 0.75, ...rubikfont }}>{title}</Typography>
                  <Box
                    sx={{
                      fontSize: "13px", color: "#475569", lineHeight: 1.55, p: 1.5, borderRadius: "10px", ...rubikfont,
                      bgcolor: tone === "warm" ? "#fdf8f0" : "#f8fafc",
                      border: tone === "warm" ? "1px solid #fae8c8" : "1px solid #eef1f5",
                      "& ul, & ol": { paddingLeft: "18px", margin: "4px 0" },
                      "& li": { marginBottom: "4px" },
                      "& p": { margin: "0 0 6px" },
                    }}
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                </Box>
              );

              return (
                <Box sx={{ display: "grid", gap: { xs: 1.5, md: 2.5 }, gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "250px minmax(0, 1fr)" }, alignItems: "start" }}>
                  {/* Product card */}
                  <Box sx={{ p: 1.5, borderRadius: "12px", bgcolor: "#faf8f3", border: "1px solid #eee7d6" }}>
                    <Box sx={{ height: { xs: 170, md: 190 }, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#fff", borderRadius: "10px", p: 1, border: "1px solid #f1ede2", mb: 1.25 }}>
                      <img src={p.product_images?.[0] || ""} alt={p.title} style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "14.5px", color: "#0f172a", lineHeight: 1.3, ...rubikfont }}>{p.title}</Typography>
                    <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 1, mt: 0.75, mb: 1.25 }}>
                      {pPrice ? <Typography sx={{ fontSize: "17px", fontWeight: 700, color: "#0f172a", ...rubikfont }}>₹{pPrice}</Typography> : null}
                      {pMrp && pMrp > pPrice ? <Typography sx={{ fontSize: "13px", color: "#94a3b8", textDecoration: "line-through", ...rubikfont }}>₹{pMrp}</Typography> : null}
                      <Chip label={`× ${activeItem.quantity || 1}`} size="small" sx={{ ml: "auto", height: 20, bgcolor: "#e0f2fe", color: "#0369a1", fontWeight: 700, fontSize: "11px" }} />
                    </Box>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="small"
                      onClick={() => navigate(`/product/${p._id}`)}
                      endIcon={<LaunchOutlinedIcon />}
                      sx={{ textTransform: "none", borderColor: "#C8A054", color: "#a8832f", fontWeight: 600, borderRadius: "8px", "&:hover": { borderColor: "#997327", bgcolor: "rgba(200,160,84,0.08)" } }}
                    >
                      View full product page
                    </Button>
                  </Box>

                  {/* Details */}
                  <Box sx={{ minWidth: 0, display: "grid", gap: 1.5 }}>
                    {/* Quick facts as compact chips, not a wide table */}
                    <Box sx={{ display: "grid", gap: 0.75, gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" } }}>
                      {facts.map((x) => (
                        <Box key={x.label} sx={{ px: 1.25, py: 0.75, borderRadius: "10px", bgcolor: "#f8fafc", border: "1px solid #eef1f5", minWidth: 0 }}>
                          <Typography sx={{ fontSize: "10.5px", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px", ...rubikfont }}>{x.label}</Typography>
                          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", textTransform: "capitalize", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...rubikfont }} title={x.value}>{x.value}</Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Highlights and how-to side by side when both exist */}
                    {(p.key_features || p.how_to_use) && (
                      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: p.key_features && p.how_to_use ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)" } }}>
                        {p.key_features && block("Key highlights", p.key_features)}
                        {p.how_to_use && block("How to use", p.how_to_use, "warm")}
                      </Box>
                    )}

                    {/* Description, folded when long */}
                    {p.description && (
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ position: "relative", maxHeight: longDesc && !descOpen ? 118 : "none", overflow: "hidden" }}>
                          {block("Description", p.description)}
                          {longDesc && !descOpen && (
                            <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 44, background: "linear-gradient(to bottom, rgba(255,255,255,0), #fff)" }} />
                          )}
                        </Box>
                        {longDesc && (
                          <Typography onClick={() => setDescOpen((v) => !v)} sx={{ mt: 0.5, fontSize: "12.5px", fontWeight: 700, color: "#a8832f", cursor: "pointer", display: "inline-block", ...rubikfont }}>
                            {descOpen ? "Show less ▲" : "Read more ▼"}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })()}
          </Box>
        )}
      </Box>


      {/* ── SIMILAR COMBOS ───────────────────────────────── */}
      {similarCombos.length > 0 && (
        <Box sx={{ maxWidth: "1440px", margin: "0 auto", px: { xs: 2, md: "24px" }, mt: 4, mb: 3 }}>
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: "3px", height: "22px", bgcolor: "#C8A054", borderRadius: "3px" }} />
            <Typography sx={{ fontSize: { xs: "18px", md: "22px" }, fontWeight: 700, color: "#111", ...rubikfont }}>
              Similar Combos
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              overflowX: "auto",
              pt: 1.25,
              px: 0.75,
              mx: -0.75,
              pb: 2,
              scrollbarWidth: "thin",
              scrollbarColor: "#C8A054 #f0f0f0",
              "&::-webkit-scrollbar": { height: 4 },
              "&::-webkit-scrollbar-thumb": { background: "#C8A054", borderRadius: 2 },
            }}
          >
            {similarCombos.slice(0, 8).map((c) => (
              <ComboCard key={c._id} combo={c} isBusiness={user?.customerType === "business"} width={{ xs: 160, sm: 190, md: 210 }} scrollTop />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ComboDetails;
